import axios from '@/http/axios';
import router from '@/router';
import store from '@/store';
import Anzsco from './Anzsco';
import Auth from './Auth';
import BookingCandidates from './BookingCandidates';
import Bookings from './Bookings';
import Businesses from './Businesses';
import BusinessContacts from './BusinessContacts';
import BusinessAddresses from './BusinessAddresses';
import BusinessCreditChecks from './BusinessCreditChecks';
import Candidates from './Candidates';
import CandidateProfiles from './CandidateProfiles';
import Questions from './Questions';
import QuestionGroups from './QuestionGroups';
import QuestionnaireAnswers from './QuestionnaireAnswers';
import Questionnaires from './Questionnaires';
import QuestionDependencies from './QuestionDependencies';
import QuestionNegativeRules from './QuestionNegativeRules';
import RiskProfiles from './RiskProfiles';
import RiskProfileTemplates from './RiskProfileTemplates';
import ShareCodes from './ShareCodes';
import Countries from './Countries';
import Ali from './Ali';
import MediaFiles from './MediaFiles';
import BodyParts from './BodyParts';
import BodySystems from './BodySystems';
import RiskProfileItems from './RiskProfileItems';
import RiskProfileSections from './RiskProfileSections';
import Documents from './Documents';
import Users from './Users';
import ServiceItems from './ServiceItems';
import ServiceOrders from './ServiceOrders';
import ServicePayments from './ServicePayments';
import BusinessCreditLevels from './BusinessCreditLevels';
import ServiceRequests from './ServiceRequests';
import ServiceCandidates from './ServiceCandidates';
import Authorizations from './Authorizations';
import ServicePaymentTransactions from './ServicePaymentTransactions';
import Gsts from './Gsts';
import FingerprintJS from '@fingerprintjs/fingerprintjs-pro';
import ab2str from 'arraybuffer-to-string';
import BusinessTag from './BusinessTag';
import SystemData from './SystemData';
import BusinessOAuthClient from './BusinessOAuthClient';
import UserPreference from './UserPreference';
import Feedback from './Feedback';

export const abortController = {
  controller: new AbortController(),
  reset() {
    this.controller = new AbortController();
  },
};

function errorHandler(redirectTo, error, params = {}) {
  const { response } = error;
  const activeRoleKey = localStorage.getItem('active_role_key');
  switch (redirectTo) {
    // case 'error-session-time-out':
    //   router.push({
    //     name: redirectTo,
    //     params: {
    //       content: response.data.error,
    //       activeRoleKey: activeRoleKey
    //     }
    //   })
    //   break
    case 'login':
      switch (activeRoleKey) {
        case 'rAdmin':
          redirectTo =
            process.env.VUE_APP_WP_VERSION === 'v2'
              ? 'v2-login'
              : 'administrator-login';
          break;
        case 'rBusiness':
          redirectTo =
            process.env.VUE_APP_WP_VERSION === 'v2'
              ? 'v2-login'
              : 'business-login';
          break;
        case 'rCandidate':
          redirectTo =
            process.env.VUE_APP_WP_VERSION === 'v2'
              ? 'v2-login'
              : 'candidate-login';
          break;
        default:
          redirectTo =
            process.env.VUE_APP_WP_VERSION === 'v2' ? 'v2-login' : 'login';
          break;
      }
      router.push({
        name: redirectTo,
        params,
      });
      break;
    case 'error-api-request':
      router.push({
        name: redirectTo,
        params: {
          content: response,
        },
      });
      break;
    case 'error-response-invalid':
      router.push({
        name: redirectTo,
        params: {
          content: error.toJSON(),
        },
      });
      break;
    case 'error-throttle':
      if (error.config.url === '/auth/details') {
        router.push({
          name: redirectTo,
        });
      }
      break;
  }
}

export default {
  init() {
    axios.interceptors.request.use(
      (config) => {
        abortController.reset();
        config.signal = abortController.controller.signal;

        store.commit('SET_LOADING', true);
        const activeRoleKey = store.state['Auth'].activeRoleKey;
        const globalEndPoints = [
          '/auth/logout',
          '/countries',
          '/body-parts',
          '/auth/details',
          '/auth/update',
          '/auth/change-password',
          '/auth/signature',
          '/documents/privacy-statement',
          '/documents/privacy-policy',
          '/documents/terms-and-conditions',
          '/documents/welcome-text-template',
          '/documents/additional-services',
          '/documents/quescreen-business-user-instructions',
          '/documents/quescreen-candidate-user-instructions',
          '/service-items',
          '/business-credit-levels',
          '/gsts',
        ];
        if (
          activeRoleKey !== 'rNoActiveRole' &&
          config.url &&
          !globalEndPoints.includes(config.url)
        ) {
          let role = store.getters['Auth/userRole'];
          role = role.toLowerCase().substring(0, 3);
          config.baseURL += '/' + role;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    axios.interceptors.response.use(
      function(response) {
        store.commit('SET_LOADING', false);
        if (response.data.code !== 'fetched') {
          // store.commit('SET_SNACKBAR', {
          //   show: true,
          //   type: 'success',
          //   message: 'Success!'
          // })
        }
        return response;
      },
      function(error) {
        store.commit('SET_LOADING', false);
        // store.commit('SET_SNACKBAR', {
        //   show: true,
        //   type: 'error',
        //   message: 'Oops! Something went wrong!'
        // })

        const isNoGlobalErrorHandler =
          error.response?.config.headers['no-global-error-handler'];
        if (isNoGlobalErrorHandler || error.code === 'ERR_CANCELED') {
          // server side validation error: catch the error in component
          return Promise.reject(error);
        }

        let isResponseError = true;

        try {
          if (!!error.code && error.code === 'ERR_NETWORK') {
            isResponseError = false;

            store.commit('SET_SNACKBAR', {
              show: true,
              type: 'error',
              message: error.message,
            });

            return Promise.reject(error);
          }

          if (error.response.config.responseType == 'arraybuffer') {
            const str = ab2str(error.response.data);
            error.response.data = JSON.parse(str);
          }

          if (
            typeof error.response !== 'undefined' &&
            typeof error.response.status !== 'undefined' &&
            typeof error.response.data != 'undefined' &&
            typeof error.response.data.code != 'undefined'
          ) {
            isResponseError = false;
          }
        } catch (exception) {
          isResponseError = true;
        }

        if (isResponseError) {
          errorHandler('error-response-invalid', error);
        } else {
          const { response } = error;
          const isValidationError = response.status === 440;
          const isUserInputError =
            response.status === 400 &&
            response.data.is_user_input_error &&
            response.data.is_user_input_error === true;
          const isUnauthenticated =
            (response.data.code &&
              response.data.code.includes('error.auth.unauthorized-user')) ||
            response.status === 401;
          const hasToken = localStorage.getItem('access_token');
          const isRateLimitException = response.status === 429;

          if (isValidationError || isUserInputError) {
            // server side validation error: catch the error in component
            return Promise.reject(error);
          } else if (isUnauthenticated && hasToken) {
            store.commit('Auth/CLEAR_STATE');
            return errorHandler('login', error, {
              session_timed_out: 1,
            });
            // user access token invalid and has access token stored in local storage:
            // redirect to error-session-time-out page
            // errorHandler('error-session-time-out', error)
          } else if (isUnauthenticated) {
            // user access token invalid but hasn't access token stored in local storage:
            // redirect to login page
            errorHandler('login', error);
          } else if (isRateLimitException) {
            const { headers } = response;
            console.debug(headers['retry-after']);
            store.commit('SET_RATE_LIMIT_EXCEPTION', true);
            store.commit(
              'SET_RATE_LIMIT_RETRY_AFTER',
              parseInt(headers['retry-after'])
            );
            errorHandler('error-throttle', error);
          } else {
            // other api error (
            //  error.forbidden 403
            //  error.route-not-found 404
            //  error.bad-request 400
            //  error.internal 500
            // } :
            // rediret to error-api-request page
            errorHandler('error-api-request', error);
          }
        }
      }
    );
  },
  initBearerToken() {
    const accessToken = store.state['Auth'].bearerAccessToken;
    if (accessToken) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${accessToken}`;
    }

    if (process.env.VUE_APP_FINGERPRINTJS_BROWSER_TOKEN) {
      // Initialize an agent at application startup.
      const fpPromise = FingerprintJS.load({
        token: process.env.VUE_APP_FINGERPRINTJS_BROWSER_TOKEN,
      });

      // Get the visitor identifier when you need it.
      fpPromise
        .then((fp) => fp.get({ extendedResult: true }))
        .then((result) => {
          axios.defaults.headers.common['Device-Uid'] = result.visitorId;
          axios.defaults.headers.common['Device-Name'] = result.os;
        });
    }
  },
  ali: Ali,
  anzsco: Anzsco,
  auth: Auth,
  bcan: BookingCandidates,
  boo: Bookings,
  bus: Businesses,
  ba: BusinessAddresses,
  bc: BusinessContacts,
  bcc: BusinessCreditChecks,
  can: Candidates,
  q: Questions,
  qg: QuestionGroups,
  qna: QuestionnaireAnswers,
  qn: Questionnaires,
  qd: QuestionDependencies,
  qnr: QuestionNegativeRules,
  rp: RiskProfiles,
  rpt: RiskProfileTemplates,
  sc: ShareCodes,
  coun: Countries,
  mf: MediaFiles,
  bp: BodyParts,
  bs: BodySystems,
  cp: CandidateProfiles,
  rpi: RiskProfileItems,
  rps: RiskProfileSections,
  doc: Documents,
  user: Users,
  si: ServiceItems,
  so: ServiceOrders,
  sp: ServicePayments,
  bcl: BusinessCreditLevels,
  sr: ServiceRequests,
  scan: ServiceCandidates,
  authz: Authorizations,
  spt: ServicePaymentTransactions,
  gst: Gsts,
  bt: BusinessTag,
  sd: SystemData,
  boc: BusinessOAuthClient,
  up: UserPreference,
  fb: Feedback,
};
